import normalizeSearchText from "~/helpers/search/normalizeSearchText";

const WORD_SCORE = { exact: 100, prefix: 80, substring: 60, typo: 40 } as const;

const COMBINING_MARKS = /[̀-ͯ]/g;
const NON_WORD = /[^\p{L}\p{N}]+/u;
const EDGE_NON_WORD = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;
const WORD_CHARACTER = /[\p{L}\p{N}]/u;

const normalizeForMatch = (value: string): string =>
	normalizeSearchText(value).normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase();

const PLAIN_ASCII = /^[\x20-\x7e\n\t]*$/;

const normalizePlainOrFull = (value: string): string =>
	PLAIN_ASCII.test(value) && !value.includes("&") ? value.toLowerCase() : normalizeForMatch(value);

const splitNormalizedWords = (normalized: string): string[] =>
	normalized.split(NON_WORD).filter((word) => word.length > 0);

const splitWords = (value: string): string[] => splitNormalizedWords(normalizeForMatch(value));

const trimPunctuation = (piece: string): string => piece.replace(EDGE_NON_WORD, "");

const isLiteralTerm = (term: string): boolean => NON_WORD.test(term);

const singularize = (word: string): string => {
	if (word.length > 3 && word.endsWith("ies")) return `${word.slice(0, -3)}y`;
	if (word.length > 4 && /(s|x|z|ch|sh)es$/.test(word)) return word.slice(0, -2);
	if (word.length > 3 && word.endsWith("s")) return word.slice(0, -1);
	return word;
};

const countAllowedTypos = (word: string): number => (word.length >= 8 ? 2 : word.length >= 4 ? 1 : 0);

const measureEditDistance = (a: string, b: string, max: number): number => {
	if (Math.abs(a.length - b.length) > max) return max + 1;
	let twoBack: number[] = [];
	let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
	for (let i = 1; i <= a.length; i++) {
		const current = [i];
		for (let j = 1; j <= b.length; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;
			let cellDistance = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
			if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
				cellDistance = Math.min(cellDistance, twoBack[j - 2] + 1);
			current.push(cellDistance);
		}
		twoBack = previous;
		previous = current;
	}
	return previous[b.length];
};

const scoreForm = (query: string, target: string, minPartialLength: number, shouldAllowTypos: boolean): number => {
	if (query === target) return WORD_SCORE.exact;
	if (query.length >= minPartialLength && target.startsWith(query)) return WORD_SCORE.prefix;
	if (query.length >= minPartialLength && target.includes(query)) return WORD_SCORE.substring;
	if (!shouldAllowTypos) return 0;
	const maxTypos = countAllowedTypos(query);
	if (maxTypos === 0) return 0;
	const wholeWordDistance = measureEditDistance(query, target, maxTypos);
	if (wholeWordDistance <= maxTypos) return WORD_SCORE.typo;
	if (target.length <= query.length) return 0;
	return measureEditDistance(query, target.slice(0, query.length), maxTypos) <= maxTypos ? WORD_SCORE.typo : 0;
};

const scoreLiteral = (literal: string, text: string): number => {
	let score = 0;
	for (let index = text.indexOf(literal); index !== -1; index = text.indexOf(literal, index + 1)) {
		const isWholeTerm =
			!WORD_CHARACTER.test(text.charAt(index - 1)) && !WORD_CHARACTER.test(text.charAt(index + literal.length));
		if (isWholeTerm) return WORD_SCORE.exact;
		score = WORD_SCORE.substring;
	}
	return score;
};

interface QueryWord {
	isLiteral: boolean;
	singular: string;
	text: string;
}

const toQueryWord = (word: string): QueryWord => {
	const text = normalizeForMatch(word);
	return { isLiteral: isLiteralTerm(text), singular: singularize(text), text };
};

const scoreNormalizedWords = (
	query: QueryWord,
	target: string,
	minPartialLength: number,
	shouldAllowTypos: boolean
): number => {
	if (query.text === target) return WORD_SCORE.exact;
	const singularTarget = singularize(target);
	return Math.max(
		scoreForm(query.text, target, minPartialLength, shouldAllowTypos),
		query.singular === query.text ? 0 : scoreForm(query.singular, target, minPartialLength, shouldAllowTypos),
		singularTarget === target ? 0 : scoreForm(query.text, singularTarget, minPartialLength, shouldAllowTypos)
	);
};

export interface FuzzyMatch {
	matchedWordCount: number;
	score: number;
}

export interface FuzzyMatchOptions {
	minPartialLength?: number;
	shouldAllowTypos?: boolean;
	shouldRequireAllWords?: boolean;
}

const COVERAGE_WEIGHT = 10;
const DEFAULT_MIN_PARTIAL_LENGTH = 2;

const fuzzyMatchText = (
	queryWords: QueryWord[],
	text: string,
	{
		minPartialLength = DEFAULT_MIN_PARTIAL_LENGTH,
		shouldAllowTypos = true,
		shouldRequireAllWords = true
	}: FuzzyMatchOptions = {}
): FuzzyMatch | undefined => {
	const normalizedText = normalizeForMatch(text);
	const targetWords = splitNormalizedWords(normalizedText);
	if (queryWords.length === 0 || targetWords.length === 0) return undefined;
	const usedTargetIndexes = new Set<number>();
	let total = 0;
	let matchedWordCount = 0;
	for (const queryWord of queryWords) {
		let bestScore = 0;
		let bestIndex = -1;
		if (queryWord.isLiteral) {
			bestScore = scoreLiteral(queryWord.text, normalizedText);
		} else {
			targetWords.forEach((targetWord, index) => {
				const score = scoreNormalizedWords(queryWord, targetWord, minPartialLength, shouldAllowTypos);
				if (score > bestScore) {
					bestScore = score;
					bestIndex = index;
				}
			});
		}
		if (bestScore === 0) {
			if (shouldRequireAllWords) return undefined;
			continue;
		}
		matchedWordCount++;
		total += bestScore;
		if (bestIndex >= 0) usedTargetIndexes.add(bestIndex);
	}
	if (matchedWordCount === 0) return undefined;
	const coverage = usedTargetIndexes.size / targetWords.length;
	return {
		matchedWordCount,
		score: total / queryWords.length + coverage * COVERAGE_WEIGHT
	};
};

export interface FuzzyRanked<TItem> extends FuzzyMatch {
	item: TItem;
}

interface RankedWithLength<TItem> extends FuzzyRanked<TItem> {
	textLength: number;
}

export interface RankFuzzyOptions extends FuzzyMatchOptions {
	limit?: number;
}

const rankFuzzy = <TItem>(
	items: TItem[],
	getText: (item: TItem) => string,
	queryWords: string[],
	{ limit, minPartialLength, shouldAllowTypos, shouldRequireAllWords }: RankFuzzyOptions = {}
): FuzzyRanked<TItem>[] => {
	const normalizedQueryWords = queryWords.map(toQueryWord);
	const rankedMatches: RankedWithLength<TItem>[] = [];
	for (const item of items) {
		const text = getText(item);
		const match = fuzzyMatchText(normalizedQueryWords, text, {
			minPartialLength,
			shouldAllowTypos,
			shouldRequireAllWords
		});
		if (match) rankedMatches.push({ ...match, item, textLength: text.length });
	}
	rankedMatches.sort((a, b) => b.score - a.score || a.textLength - b.textLength);
	const ranked: FuzzyRanked<TItem>[] = rankedMatches.map(({ item, matchedWordCount, score }) => ({
		item,
		matchedWordCount,
		score
	}));
	return limit === undefined ? ranked : ranked.slice(0, limit);
};

const createTextMatcher = (
	queryWords: string[],
	{ minPartialLength = DEFAULT_MIN_PARTIAL_LENGTH, shouldAllowTypos = true }: FuzzyMatchOptions = {}
): ((text: string) => boolean) => {
	const normalizedQueryWords = queryWords.map((word) => ({
		...toQueryWord(word),
		seen: new Map<string, boolean>()
	}));
	return (text) => {
		if (normalizedQueryWords.length === 0) return false;
		const normalizedText = normalizePlainOrFull(text);
		let targetWords: string[] | undefined;
		for (const queryWord of normalizedQueryWords) {
			if (queryWord.text.length >= minPartialLength && normalizedText.includes(queryWord.text)) continue;
			if (queryWord.isLiteral) return false;
			targetWords ??= splitNormalizedWords(normalizedText);
			const isMatch = targetWords.some((targetWord) => {
				let hit = queryWord.seen.get(targetWord);
				if (hit === undefined) {
					hit = scoreNormalizedWords(queryWord, targetWord, minPartialLength, shouldAllowTypos) > 0;
					queryWord.seen.set(targetWord, hit);
				}
				return hit;
			});
			if (!isMatch) return false;
		}
		return true;
	};
};

export {
	countAllowedTypos,
	createTextMatcher,
	isLiteralTerm,
	measureEditDistance,
	normalizeForMatch,
	singularize,
	splitWords,
	trimPunctuation
};
export default rankFuzzy;
